/**
 * Discovery — o feed BRUTO de recomendações (tela `2d`).
 *
 * 🔒 **Separado de `Oportunidade` de propósito** (BR-02). Nada que vem das
 * ferramentas da AWS é publicado automaticamente: uma linha de Discovery precisa
 * ser PROMOVIDA por um arquiteto, e a promoção cria uma oportunidade em
 * `rascunho`, invisível ao cliente. Duas formas, dois ciclos de vida — o feed é
 * recarregado pelo batch diário, a oportunidade curada é permanente.
 *
 * ⚠️ Discovery NÃO tem `visibilidadeCliente`, e a ausência é deliberada: §3.1 do
 * PROJECT-OVERVIEW registra que o Eduardo não vê problema no Cliente enxergar a
 * camada crua do Compute Optimizer. O gate do BR-01 é sobre a camada CURADA.
 */
import { z } from 'zod'

import { dinheiro, id, valorDeCatalogo } from './comum'

export const linhaDeDiscoverySchema = z.object({
  idDiscovery: id,
  idCliente: id,

  /** Uma das quatro do catálogo: compute-optimizer, cost-optimization-hub,
   *  cudos-parquet, ia. Catálogo e não enum — uma quinta ferramenta da AWS não
   *  pode exigir deploy. */
  origemFerramenta: valorDeCatalogo,
  /** Rótulo já resolvido pelo servidor, para a tabela não fazer lookup. */
  rotuloOrigem: z.string().nullable().optional(),

  accountId: valorDeCatalogo.nullable(),
  servicoAws: valorDeCatalogo.nullable(),
  recurso: z.string().nullable(),

  /** A coluna "Recomendação" da `2d`. */
  recomendacao: z.string(),
  /** A coluna "Economia est./mês". ESTIMADA pela ferramenta — e é por isso que a
   *  promoção NÃO deriva custo atual e potencial dela: seria inventar número. */
  economiaEstimadaMensal: dinheiro.nullable(),
  /** USD, como todo valor deste módulo hoje. Coluna existe para BR-03/BR-21. */
  moeda: z.string().default('USD'),

  situacao: z.enum(['novo', 'promovido', 'descartado']),
  idOportunidade: id.nullable(),

  particaoOrigem: z.string().nullable().optional(),
  detectadoEm: z.string(),
  ingeridoEm: z.string(),
})

export type LinhaDeDiscovery = z.infer<typeof linhaDeDiscoverySchema>

/** Um chip da linha de filtro da `2d`, com a contagem dentro. */
export const contagemPorOrigemSchema = z.object({
  origem: z.string(),
  rotulo: z.string(),
  total: z.number(),
})

export type ContagemPorOrigem = z.infer<typeof contagemPorOrigemSchema>

/**
 * 🔑 A contagem vem no MESMO envelope da lista, e é calculada ANTES do filtro de
 * origem. Se viesse depois, o chip não selecionado mostraria "(0)" e o usuário
 * concluiria que aquela fonte não trouxe nada. Endpoint separado seria uma
 * segunda chamada garantida em toda montagem de tela — e a chance de os dois
 * números discordarem.
 */
export const respostaDeDiscoverySchema = z.object({
  itens: z.array(linhaDeDiscoverySchema),
  total: z.number(),
  pagina: z.number(),
  tamanho: z.number(),
  contagemPorOrigem: z.array(contagemPorOrigemSchema),
  podePromover: z.boolean(),
})

export type RespostaDeDiscovery = z.infer<typeof respostaDeDiscoverySchema>

/**
 * Resultado PARCIAL da promoção. Promover 20 e falhar em 1 não pode desfazer as
 * 19 — o arquiteto teria que descobrir qual falhou e reprocessar o resto à mão.
 */
export const resultadoDaPromocaoSchema = z.object({
  criadas: z.array(z.object({ idDiscovery: id, idOportunidade: id, codigo: z.string() })),
  ignoradas: z.array(
    z.object({
      idDiscovery: id,
      motivo: z.enum(['JA_PROMOVIDA', 'NAO_ENCONTRADA']),
      idOportunidade: id.optional(),
    }),
  ),
})

export type ResultadoDaPromocao = z.infer<typeof resultadoDaPromocaoSchema>

/**
 * `POST /finops/discovery/gerar-ia` — fonte: `casos_uso/discovery.py::gerar_ia`.
 *
 * `status` é `string`, e não literal, pelo mesmo motivo de `mensagemAceitaSchema`
 * (contratos/mensagens.ts): ninguém lê este campo no 202, só o `idExecucao`.
 */
export const execucaoIaAceitaSchema = z.object({
  idExecucao: id,
  status: z.string(),
})

export type ExecucaoIaAceita = z.infer<typeof execucaoIaAceitaSchema>

/**
 * `GET /finops/discovery/execucoes/{idExecucao}` — fonte:
 * `repositorio/postgres.py::obter_execucao_ia`. Igual ao chat
 * (`statusDaMensagemSchema`), um `discriminatedUnion` por `status`: o CHECK do
 * banco (`V0019`) garante `quantidadeSugestoes` presente em `concluida` e
 * `erroCodigo`/`erroMensagem` presentes em `falha` — nunca os três juntos.
 */
export const statusDaExecucaoIaSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('processando') }),
  z.object({ status: z.literal('concluida'), quantidadeSugestoes: z.number() }),
  z.object({
    status: z.literal('falha'),
    erroCodigo: z.string(),
    erroMensagem: z.string(),
  }),
])

export type StatusDaExecucaoIa = z.infer<typeof statusDaExecucaoIaSchema>
